/// <amd-module name="Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/reasonCodeOperationModal/reasonCodeOperationModal"/>
import { Button, ButtonGroup, DialogPart, EmptyState, FlexLayout, FlexSpacer, ImageMessagessCleaninbox, InstantSearchBox, LocalizationContext, ModalDialogShell, StringMap, Text } from '@medius/ui-controls';
import * as React from 'react';
import ReasonCodesGrid from 'Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/shared/reasonCodesGrid';
import { IInvoiceReasonCodeData, IReasonCode } from '../rejectionManagementWizard/interfaces';
import { translate } from 'Core/Medius.Core.Web/Scripts/lib/globalization';
import { ReasonCodeOperationType } from '../shared/interfaces';
import { useInvoiceReasonCodeData } from '../rejectionManagementWizard/useInvoiceReasonCodeData';
import { postPutOnHoldCodesDto } from '../putOnHoldModal/services';
import { useRejectionManagementEmailForm } from '../rejectionManagementWizard/useRejectionManagementEmailForm';
import { PutOnHoldReasonsDto } from 'Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/putOnHoldModal/interfaces';
import { getTranslatedDocumentType } from '../shared/utils';
import { EntityType } from "Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/shared/interfaces";
import { getCultureCode } from 'Core/Medius.Core.Web/Scripts/Medius/lib/utils/user';
import { mapFromNorthstarToApa } from 'Core/Medius.Core.Web/Scripts/lib/northstarLocalization';

interface ReasonCodeOperationModalProps {
    documentId: number;
    onClose: (wasConfirmed: boolean) => void;
    modalType: ReasonCodeOperationType;
    hierarchyId: string;
    task: string;
    entityType: EntityType;
    currentDocumentType: string;
}

export const translateKeyBase = "#PurchaseToPay/reasonCodeOperationModal";

export const ReasonCodeOperationModal = ({ 
    documentId, 
    onClose, 
    modalType, 
    hierarchyId,
    task,
    entityType,
    currentDocumentType
}: ReasonCodeOperationModalProps) => {
    const searchLabelId = "searchLabelId";
    const [selection, setSelection] = React.useState<IReasonCode[]>([]);
    const invoiceReasonCodeData: IInvoiceReasonCodeData = useInvoiceReasonCodeData(modalType, hierarchyId);
    const [searchTerm, setSearchTerm] = React.useState("");
    const { searching: { changeSearchTerm: setSearch } } = invoiceReasonCodeData;
    const isSelectionMandatory = selection?.length === 0;

    const formContext = useRejectionManagementEmailForm();
    const { handleSubmit } = formContext;

    const getModalTitle = () => {
        return translate(`${translateKeyBase}/onHoldInvoice`);
    };

    const getSearchBoxTitle = () => {
        return translate(`${translateKeyBase}/selectReasonForOnHold`);
    };

    const getSubmitButtonLabel = () => {
        return translate("#Core/putOnHold");
    };

    const handleSubmitButton = handleSubmit(async () => {
        const dto: PutOnHoldReasonsDto = {
            documentId: documentId,
            invoiceRejectionReasonItems: selection.map(rc => ({
                    reasonCodeId: rc.id,
                    actionId: rc.rejectionActionId
                })),
            operation: translate("#PurchaseToPay/documentReasonCodes/operations/OnHold"),
            task: task,
            documentType: getTranslatedDocumentType(currentDocumentType),
            putOnHoldEntityType: entityType
        };

        await postPutOnHoldCodesDto(dto);

        onClose(true);
    });

    const noReasonCodesAvailable = searchTerm === "" && invoiceReasonCodeData.invoiceReasonCodes.length === 0 && !invoiceReasonCodeData.isLoading;

    return (
        <LocalizationContext.Provider
            value={{
                translate: (key: string, _, defaultValue?: string, args?: StringMap) =>
                    mapFromNorthstarToApa(key, defaultValue, args),
                getLocale: () => getCultureCode()
            }}
        >
            <ModalDialogShell
                isOpen
                hasHeader
                height={'vlarge'}
                title={getModalTitle()}
                data-testid="reason-code-operation-modal"
                onRequestClose={() => onClose(false)} >
                {dialogProps => <>
                    <DialogPart.Title {...dialogProps.headerProps} />
                    <DialogPart.Content width="vlarge">
                        <div>
                            {noReasonCodesAvailable ? 
                                <FlexLayout verticalAlignment='center' horizontalAlignment='center' height='fill' width='fill'>
                                    <EmptyState 
                                        contentAlign="center"
                                        title={translate(`${translateKeyBase}/noReasonCodesAvailableTitle`)}
                                        explanation={translate(`${translateKeyBase}/noReasonCodesAvailableExplanation`)}
                                        image={<ImageMessagessCleaninbox />}
                                        size='large'
                                    />
                                </FlexLayout>
                                : 
                                <>
                                    <div>
                                        <Text id={searchLabelId} variant="heading3" data-testid='reason-code-operation-modal-search-header'>{getSearchBoxTitle()}</Text>
                                        <InstantSearchBox
                                            data-testid="reason-code-operation-modal-search-box"
                                            labelId={searchLabelId}
                                            width="fill"
                                            spacerMode="off"
                                            executeSearch={(searchTerm) => { 
                                                setSearch(searchTerm); 
                                                setSearchTerm(searchTerm);}} 
                                        />
                                    </div>
                                    <div>
                                        <ReasonCodesGrid
                                            selection={selection}
                                            setSelection={setSelection}
                                            invoiceReasonCodeData={invoiceReasonCodeData}
                                            reasonCodeOperationType={modalType}
                                        />
                                    </div>
                                </>
                            }
                        </div>
                    </DialogPart.Content>
                    <DialogPart.Footer>
                        <FlexLayout>
                            <FlexSpacer />
                            <ButtonGroup>
                                <Button
                                    data-testid="reason-code-operation-modal-cancel-button"
                                    label={translate("#Core/cancel")}
                                    variant="secondary"
                                    onClick={() => onClose(false)} />
                                <Button
                                    data-testid="reason-code-operation-modal-submit-button"
                                    label={getSubmitButtonLabel()}
                                    disabled={isSelectionMandatory}
                                    onClick={handleSubmitButton}
                                />
                            </ButtonGroup>
                        </FlexLayout>
                    </DialogPart.Footer>
                </>}
            </ModalDialogShell>
        </LocalizationContext.Provider>
    );
};